import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api';
import { downloadFile } from '../lib/download';
import Modal from '../components/Modal';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import {
  FileText,
  Download,
  Plus,
  Calendar,
  Loader2,
  Trash2,
  Pencil,
  Receipt as ReceiptIcon,
  IndianRupee,
  Paperclip,
  Eye,
  X,
} from 'lucide-react';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const fmtINR = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);

const emptyReceipt = {
  date: new Date().toISOString().split('T')[0],
  client_id: '',
  client_name: '',
  project_id: '',
  project_name: '',
  description: '',
  amount: '',
  mode: 'cash',
  reference: '',
  notes: '',
};

const MonthlyStatementPage = () => {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [clients, setClients] = useState([]);
  const [architects, setArchitects] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [editingReceiptId, setEditingReceiptId] = useState(null);
  const [receiptDraft, setReceiptDraft] = useState(emptyReceipt);
  const [savingReceipt, setSavingReceipt] = useState(false);
  const [receiptAttachment, setReceiptAttachment] = useState(null); // existing attachment info {name, filename}
  const [pendingFile, setPendingFile] = useState(null); // File chosen in modal, uploaded after save
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const receiptFileRef = useRef(null);

  const loadStatement = async () => {
    setLoading(true);
    try {
      const r = await api.get('/reports/monthly-statement', { params: { month, year } });
      setData(r.data);
    } catch (err) {
      toast.error('Failed to load monthly statement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatement(); /* eslint-disable-line */ }, [month, year]);

  useEffect(() => {
    const loadStatic = async () => {
      try {
        const [c, p, a] = await Promise.all([api.get('/clients'), api.get('/projects'), api.get('/architects')]);
        setClients(c.data || []);
        setProjects(p.data || []);
        setArchitects(a.data || []);
      } catch (err) {
        console.error(err);
      }
    };
    loadStatic();
  }, []);

  // Unified client/architect list for the receipt "Client" dropdown
  const clientArchitectOptions = useMemo(() => {
    const cs = (clients || []).map((c) => ({ id: c.id, name: c.name, isArchitect: false }));
    const as = (architects || []).map((a) => ({ id: a.id, name: a.name, isArchitect: true }));
    return [...cs, ...as].sort((x, y) => (x.name || '').localeCompare(y.name || ''));
  }, [clients, architects]);

  const openReceiptModal = (receipt = null) => {
    setPendingFile(null);
    setReceiptAttachment(
      receipt && receipt.attachment_filename
        ? { name: receipt.attachment_name || 'Attachment', filename: receipt.attachment_filename }
        : null
    );
    if (receipt) {
      setEditingReceiptId(receipt.id);
      setReceiptDraft({
        date: receipt.date || new Date().toISOString().split('T')[0],
        client_id: receipt.client_id || '',
        client_name: receipt.client_name || '',
        project_id: receipt.project_id || '',
        project_name: receipt.project_name || '',
        description: receipt.description || '',
        amount: receipt.amount || '',
        mode: receipt.mode || 'cash',
        reference: receipt.reference || '',
        notes: receipt.notes || '',
      });
    } else {
      setEditingReceiptId(null);
      setReceiptDraft({ ...emptyReceipt, date: new Date().toISOString().split('T')[0] });
    }
    setShowReceiptModal(true);
  };

  const handleSaveReceipt = async () => {
    if (!receiptDraft.date || !receiptDraft.amount || Number(receiptDraft.amount) <= 0) {
      toast.error('Date and Amount are required');
      return;
    }
    setSavingReceipt(true);
    try {
      const payload = {
        ...receiptDraft,
        amount: Number(receiptDraft.amount),
      };
      let savedId = editingReceiptId;
      if (editingReceiptId) {
        await api.put(`/receipts/${editingReceiptId}`, payload);
        toast.success('Receipt updated');
      } else {
        const r = await api.post('/receipts', payload);
        savedId = r.data?.id;
        toast.success('Receipt recorded');
      }
      // Upload attachment chosen in the modal (after receipt exists)
      if (pendingFile && savedId) {
        const fd = new FormData();
        fd.append('file', pendingFile);
        await api.post(`/receipts/${savedId}/attachment`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      setShowReceiptModal(false);
      setPendingFile(null);
      loadStatement();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save receipt');
    } finally {
      setSavingReceipt(false);
    }
  };

  const handleViewAttachment = async (receiptId) => {
    try {
      const res = await api.get(`/receipts/${receiptId}/attachment`, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(res.data);
      window.open(blobUrl, '_blank', 'noopener');
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      toast.error('Could not open attachment');
    }
  };

  const handleRemoveAttachment = async (receiptId) => {
    const res = await Swal.fire({
      title: 'Remove attachment?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      confirmButtonText: 'Yes, remove',
    });
    if (!res.isConfirmed) return;
    try {
      await api.delete(`/receipts/${receiptId}/attachment`);
      setReceiptAttachment(null);
      toast.success('Attachment removed');
      loadStatement();
    } catch (err) {
      toast.error('Failed to remove attachment');
    }
  };

  const handleDeleteReceipt = async (receipt) => {
    const res = await Swal.fire({
      title: 'Delete Receipt?',
      text: `Receipt ${receipt.receipt_no} of ${fmtINR(receipt.amount)} will be deleted.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      confirmButtonText: 'Yes, delete',
    });
    if (!res.isConfirmed) return;
    try {
      await api.delete(`/receipts/${receipt.id}`);
      toast.success('Receipt deleted');
      loadStatement();
    } catch (err) {
      toast.error('Failed to delete receipt');
    }
  };

  const handleExport = async (kind) => {
    setExporting(true);
    try {
      const url = `/reports/monthly-statement/${kind}?month=${month}&year=${year}`;
      const ext = kind === 'excel' ? 'xlsx' : 'pdf';
      await downloadFile(url, `Monthly_Statement_${year}_${String(month).padStart(2, '0')}.${ext}`);
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const filteredProjects = useMemo(() => {
    if (!receiptDraft.client_id) return projects;
    // Match either client_id or architect_id (unified selector)
    return projects.filter((p) => p.client_id === receiptDraft.client_id || p.architect_id === receiptDraft.client_id);
  }, [projects, receiptDraft.client_id]);

  const t = data?.totals;

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="monthly-statement-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-head text-3xl font-extrabold" style={{ color: 'var(--cc-dark-green)' }}>
            Monthly Statement
          </h1>
          <p className="text-sm" style={{ color: 'var(--cc-text-muted)' }}>
            Billed, Without-Bill, and Payments for the selected month.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-2 [&_.btn]:w-full sm:[&_.btn]:w-auto">
          <button
            onClick={() => openReceiptModal(null)}
            className="btn btn-primary"
            data-testid="btn-record-receipt"
          >
            <Plus size={16} /> Record Receipt (Without Bill)
          </button>
          <button
            onClick={() => handleExport('excel')}
            disabled={exporting || !data}
            className="btn btn-outline"
            data-testid="btn-export-statement-excel"
          >
            <Download size={16} /> {exporting ? 'Exporting…' : 'Export Excel'}
          </button>
          <button
            onClick={() => handleExport('pdf')}
            disabled={exporting || !data}
            className="btn btn-outline"
            data-testid="btn-export-statement-pdf"
          >
            <FileText size={16} /> Export PDF
          </button>
        </div>
      </div>

      {/* Month / Year picker */}
      <div className="card p-3 mb-4 border" style={{ borderColor: 'var(--cc-border)' }}>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] font-semibold text-gray-600 uppercase mb-1 block">Month</label>
            <select
              className="input h-9 text-xs"
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              data-testid="statement-month-select"
            >
              {MONTHS.map((m, i) => (
                <option key={m} value={i + 1}>{m}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-semibold text-gray-600 uppercase mb-1 block">Year</label>
            <select
              className="input h-9 text-xs"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              data-testid="statement-year-select"
            >
              {Array.from({ length: 6 }, (_, i) => now.getFullYear() - i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div className="text-xs text-gray-500 flex items-center gap-1 ml-2">
            <Calendar size={14} /> {data?.period ? `${data.period.from} → ${data.period.to}` : '—'}
          </div>
        </div>
      </div>

      {loading && (
        <div className="text-center py-12 flex items-center justify-center gap-2 text-sm" style={{ color: 'var(--cc-text-muted)' }}>
          <Loader2 className="animate-spin" size={16} /> Loading statement…
        </div>
      )}

      {!loading && data && (
        <>
          {/* Totals Summary */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="statement-totals">
            <div className="card p-4 border-l-4" style={{ borderLeftColor: '#10b981' }}>
              <div className="text-[10px] uppercase text-gray-500 font-semibold">Billed (Tax)</div>
              <div className="text-2xl font-bold font-mono-data" style={{ color: 'var(--cc-dark-green)' }}>{fmtINR(t.billed.payable)}</div>
              <div className="text-xs text-gray-500 mt-1">Received: <span className="font-semibold text-emerald-700">{fmtINR(t.billed.received)}</span></div>
              <div className="text-xs text-gray-500">Balance: <span className="font-semibold text-red-600">{fmtINR(t.billed.balance)}</span> ({t.billed.count} invoices)</div>
            </div>
            <div className="card p-4 border-l-4" style={{ borderLeftColor: '#f59e0b' }}>
              <div className="text-[10px] uppercase text-gray-500 font-semibold">Without Bill</div>
              <div className="text-2xl font-bold font-mono-data" style={{ color: '#92400E' }}>{fmtINR(t.without_bill.amount)}</div>
              <div className="text-xs text-gray-500 mt-1">{t.without_bill.count} receipts</div>
            </div>
            <div className="card p-4 border-l-4" style={{ borderLeftColor: '#3b82f6' }}>
              <div className="text-[10px] uppercase text-gray-500 font-semibold">Total Payments</div>
              <div className="text-2xl font-bold font-mono-data" style={{ color: '#1e3a8a' }}>{fmtINR(t.payments.amount)}</div>
              <div className="text-xs text-gray-500 mt-1">{t.payments.count} entries</div>
              {Object.entries(t.payments.by_mode || {}).length > 0 && (
                <div className="text-[10px] text-gray-500 mt-1 flex flex-wrap gap-1">
                  {Object.entries(t.payments.by_mode).map(([k, v]) => (
                    <span key={k} className="px-1.5 py-0.5 bg-gray-100 rounded">{k}: {fmtINR(v)}</span>
                  ))}
                </div>
              )}
            </div>
            <div className="card p-4 border-l-4" style={{ borderLeftColor: '#8b5cf6' }}>
              <div className="text-[10px] uppercase text-gray-500 font-semibold">Grand Total Collected</div>
              <div className="text-2xl font-bold font-mono-data" style={{ color: '#5b21b6' }}>{fmtINR(t.grand_total_collected)}</div>
              <div className="text-xs text-gray-500 mt-1">Billed + Without-Bill</div>
            </div>
          </div>

          {/* Billed */}
          <div className="card overflow-hidden mb-6">
            <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: 'var(--cc-border)', background: '#F0FDF4' }}>
              <h2 className="font-head text-lg font-bold" style={{ color: 'var(--cc-dark-green)' }}>1. Billed (Tax Invoices)</h2>
              <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded">{data.billed.length}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="cc-table" data-testid="statement-billed-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Invoice No.</th>
                    <th>Client</th>
                    <th className="num" style={{ textAlign: 'right' }}>Base</th>
                    <th className="num" style={{ textAlign: 'right' }}>GST</th>
                    <th className="num" style={{ textAlign: 'right' }}>Payable</th>
                    <th className="num" style={{ textAlign: 'right' }}>Received</th>
                    <th className="num" style={{ textAlign: 'right' }}>Balance</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.billed.length === 0 ? (
                    <tr><td colSpan={9} className="text-center py-6 text-xs" style={{ color: 'var(--cc-text-muted)' }}>No tax invoices for this month.</td></tr>
                  ) : (
                    data.billed.map((r, idx) => (
                      <tr key={idx} data-testid={`billed-row-${r.invoice_no}`}>
                        <td className="text-xs font-mono-data">{r.date}</td>
                        <td className="font-mono-data font-semibold text-sm">{r.invoice_no}</td>
                        <td>{r.client_name}</td>
                        <td className="num font-mono-data">{fmtINR(r.base)}</td>
                        <td className="num font-mono-data">{fmtINR(r.gst_amount)}</td>
                        <td className="num font-mono-data font-semibold">{fmtINR(r.payable)}</td>
                        <td className="num font-mono-data text-emerald-700">{fmtINR(r.received)}</td>
                        <td className="num font-mono-data text-red-600">{fmtINR(r.balance)}</td>
                        <td>
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase"
                            style={r.payment_status === 'paid'
                              ? { background: '#D1FAE5', color: '#065F46' }
                              : r.payment_status === 'partial'
                              ? { background: '#FEF3C7', color: '#92400E' }
                              : { background: '#FEE2E2', color: '#991B1B' }}
                          >
                            {r.payment_status}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                {data.billed.length > 0 && (
                  <tfoot>
                    <tr style={{ background: '#F0FDF4', fontWeight: 700 }}>
                      <td colSpan={3}>TOTAL</td>
                      <td className="num font-mono-data">{fmtINR(t.billed.base)}</td>
                      <td className="num font-mono-data">{fmtINR(t.billed.gst)}</td>
                      <td className="num font-mono-data">{fmtINR(t.billed.payable)}</td>
                      <td className="num font-mono-data text-emerald-700">{fmtINR(t.billed.received)}</td>
                      <td className="num font-mono-data text-red-600">{fmtINR(t.billed.balance)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {/* Without-Bill */}
          <div className="card overflow-hidden mb-6">
            <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: 'var(--cc-border)', background: '#FFFBEB' }}>
              <h2 className="font-head text-lg font-bold" style={{ color: '#92400E' }}>2. Without Bill (Cash / Non-GST Receipts)</h2>
              <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-800 rounded">{data.without_bill.length}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="cc-table" data-testid="statement-without-bill-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Receipt No.</th>
                    <th>Client</th>
                    <th>Project</th>
                    <th>Mode</th>
                    <th>Reference</th>
                    <th>Description</th>
                    <th className="num" style={{ textAlign: 'right' }}>Amount</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.without_bill.length === 0 ? (
                    <tr><td colSpan={9} className="text-center py-6 text-xs" style={{ color: 'var(--cc-text-muted)' }}>No without-bill receipts for this month.</td></tr>
                  ) : (
                    data.without_bill.map((r, idx) => {
                      // find full receipt for actions
                      return (
                        <tr key={idx} data-testid={`without-bill-row-${r.receipt_no}`}>
                          <td className="text-xs font-mono-data">{r.date}</td>
                          <td className="font-mono-data font-semibold text-sm">{r.receipt_no}</td>
                          <td>{r.client_name}</td>
                          <td className="text-xs">{r.project_name || '—'}</td>
                          <td className="uppercase text-[10px] font-semibold">{r.mode}</td>
                          <td className="text-xs">{r.reference || '—'}</td>
                          <td className="text-xs">{r.description || '—'}</td>
                          <td className="num font-mono-data font-semibold">{fmtINR(r.amount)}</td>
                          <td>
                            <div className="flex gap-1 justify-center">
                              {r.attachment_filename && (
                                <button
                                  onClick={() => handleViewAttachment(r.id)}
                                  className="btn btn-outline btn-sm px-2"
                                  title="View attachment"
                                  data-testid={`btn-view-receipt-attachment-${r.receipt_no}`}
                                >
                                  <Paperclip size={12} />
                                </button>
                              )}
                              <button
                                onClick={() => openReceiptModal(r)}
                                className="btn btn-outline btn-sm px-2"
                                title="Edit Receipt"
                                data-testid={`btn-edit-receipt-${r.receipt_no}`}
                              >
                                <Pencil size={12} />
                              </button>
                              <button
                                onClick={() => handleDeleteReceipt(r)}
                                className="btn btn-danger btn-sm px-2"
                                title="Delete Receipt"
                                data-testid={`btn-delete-receipt-${r.receipt_no}`}
                              >
                                <Trash2 size={12} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
                {data.without_bill.length > 0 && (
                  <tfoot>
                    <tr style={{ background: '#FFFBEB', fontWeight: 700 }}>
                      <td colSpan={7}>TOTAL</td>
                      <td className="num font-mono-data">{fmtINR(t.without_bill.amount)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {/* Payments */}
          <div className="card overflow-hidden mb-6">
            <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: 'var(--cc-border)', background: '#EFF6FF' }}>
              <h2 className="font-head text-lg font-bold" style={{ color: '#1e40af' }}>3. Payments (Projects, Invoices & Receipts)</h2>
              <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded">{data.payments.length}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="cc-table" data-testid="statement-payments-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Source</th>
                    <th>Project Code / Ref</th>
                    <th>Client</th>
                    <th>Inv / Rcp No.</th>
                    <th>Mode</th>
                    <th>Notes</th>
                    <th className="num" style={{ textAlign: 'right' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.payments.length === 0 ? (
                    <tr><td colSpan={8} className="text-center py-6 text-xs" style={{ color: 'var(--cc-text-muted)' }}>No payments recorded in this month.</td></tr>
                  ) : (
                    data.payments.map((r, idx) => (
                      <tr key={idx}>
                        <td className="text-xs font-mono-data">{r.date}</td>
                        <td>
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase"
                            style={r.source === 'project'
                              ? { background: '#DBEAFE', color: '#1e40af' }
                              : { background: '#FEF3C7', color: '#92400E' }}
                          >
                            {r.source}
                          </span>
                        </td>
                        <td className="text-xs font-mono-data">{r.project_code || '—'}</td>
                        <td>{r.client_name || '—'}</td>
                        <td className="text-xs font-mono-data">{r.invoice_no || '—'}</td>
                        <td className="uppercase text-[10px] font-semibold">{r.mode || '—'}</td>
                        <td className="text-xs">{r.notes || '—'}</td>
                        <td className="num font-mono-data font-semibold">{fmtINR(r.amount)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
                {data.payments.length > 0 && (
                  <tfoot>
                    <tr style={{ background: '#EFF6FF', fontWeight: 700 }}>
                      <td colSpan={7}>TOTAL PAYMENTS</td>
                      <td className="num font-mono-data">{fmtINR(t.payments.amount)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </>
      )}

      {/* Receipt Modal */}
      <Modal
        open={showReceiptModal}
        onClose={() => !savingReceipt && setShowReceiptModal(false)}
        title={editingReceiptId ? 'Edit Receipt (Without Bill)' : 'Record Receipt (Without Bill)'}
      >
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label">Date <span className="text-red-500">*</span></label>
              <input
                type="date"
                className="input"
                value={receiptDraft.date}
                onChange={(e) => setReceiptDraft({ ...receiptDraft, date: e.target.value })}
                data-testid="receipt-date"
              />
            </div>
            <div>
              <label className="label">Amount (INR) <span className="text-red-500">*</span></label>
              <div className="relative">
                <IndianRupee size={14} className="absolute left-3 top-3 text-gray-400" />
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="input pl-9"
                  value={receiptDraft.amount}
                  onChange={(e) => setReceiptDraft({ ...receiptDraft, amount: e.target.value })}
                  data-testid="receipt-amount"
                />
              </div>
            </div>
            <div>
              <label className="label">Client / Architect</label>
              <select
                className="input"
                value={receiptDraft.client_id}
                onChange={(e) => {
                  const c = clientArchitectOptions.find((x) => x.id === e.target.value);
                  setReceiptDraft({ ...receiptDraft, client_id: e.target.value, client_name: c?.name || '', project_id: '', project_name: '' });
                }}
                data-testid="receipt-client"
              >
                <option value="">— Select —</option>
                {clientArchitectOptions.map((c) => (
                  <option key={c.id} value={c.id}>{c.isArchitect ? `${c.name} (Architect)` : c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Project (optional)</label>
              <select
                className="input"
                value={receiptDraft.project_id}
                onChange={(e) => {
                  const p = filteredProjects.find((x) => x.id === e.target.value);
                  setReceiptDraft({ ...receiptDraft, project_id: e.target.value, project_name: p?.name || '' });
                }}
                data-testid="receipt-project"
              >
                <option value="">— None —</option>
                {filteredProjects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Mode</label>
              <select
                className="input"
                value={receiptDraft.mode}
                onChange={(e) => setReceiptDraft({ ...receiptDraft, mode: e.target.value })}
                data-testid="receipt-mode"
              >
                <option value="cash">Cash</option>
                <option value="bank">Bank Transfer</option>
                <option value="upi">UPI</option>
                <option value="cheque">Cheque</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="label">Reference (Cheque/UTR/Txn No.)</label>
              <input
                className="input"
                value={receiptDraft.reference}
                onChange={(e) => setReceiptDraft({ ...receiptDraft, reference: e.target.value })}
                data-testid="receipt-reference"
              />
            </div>
          </div>
          <div>
            <label className="label">Description</label>
            <input
              className="input"
              value={receiptDraft.description}
              onChange={(e) => setReceiptDraft({ ...receiptDraft, description: e.target.value })}
              placeholder="e.g. Advance for site visit"
              data-testid="receipt-description"
            />
          </div>
          <div>
            <label className="label">Notes</label>
            <textarea
              className="input"
              rows={2}
              value={receiptDraft.notes}
              onChange={(e) => setReceiptDraft({ ...receiptDraft, notes: e.target.value })}
              data-testid="receipt-notes"
            />
          </div>

          {/* Attachment (cheque / UPI screenshot) */}
          <div>
            <label className="label">Attachment (photo / PDF — optional)</label>
            <input
              type="file"
              ref={receiptFileRef}
              accept="image/*,.pdf"
              className="hidden"
              onChange={(e) => setPendingFile(e.target.files?.[0] || null)}
              data-testid="receipt-attachment-input"
            />
            {receiptAttachment ? (
              <div className="flex items-center gap-2 p-2 rounded border bg-gray-50" style={{ borderColor: 'var(--cc-border)' }} data-testid="receipt-attachment-existing">
                <Paperclip size={14} className="text-gray-500" />
                <span className="text-xs font-medium truncate flex-1">{receiptAttachment.name}</span>
                {editingReceiptId && (
                  <>
                    <button
                      type="button"
                      onClick={() => handleViewAttachment(editingReceiptId)}
                      className="btn btn-outline btn-sm px-2"
                      title="View attachment"
                      data-testid="receipt-attachment-view"
                    >
                      <Eye size={12} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveAttachment(editingReceiptId)}
                      className="btn btn-danger btn-sm px-2"
                      title="Remove attachment"
                      data-testid="receipt-attachment-remove"
                    >
                      <X size={12} />
                    </button>
                  </>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => receiptFileRef.current?.click()}
                className="btn btn-outline w-full text-xs"
                data-testid="receipt-attachment-pick"
              >
                <Paperclip size={14} /> {pendingFile ? pendingFile.name : 'Attach cheque / UPI screenshot'}
              </button>
            )}
            {pendingFile && !receiptAttachment && (
              <div className="text-[11px] text-gray-500 mt-1 flex items-center gap-1">
                <Paperclip size={11} /> Will upload on save: <span className="font-semibold">{pendingFile.name}</span>
                <button type="button" className="text-red-500 ml-1" onClick={() => setPendingFile(null)} data-testid="receipt-attachment-clear">clear</button>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => setShowReceiptModal(false)}
              disabled={savingReceipt}
              className="btn btn-outline"
              data-testid="btn-cancel-receipt"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveReceipt}
              disabled={savingReceipt}
              className="btn btn-primary"
              data-testid="btn-save-receipt"
            >
              <ReceiptIcon size={14} /> {savingReceipt ? 'Saving…' : editingReceiptId ? 'Update Receipt' : 'Save Receipt'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default MonthlyStatementPage;
